import { useEffect, useState } from "react";
import type { GameSummary } from "@msf/shared";
import { api, ApiError } from "../lib/api.js";
import { useSession } from "../store/session.js";
import { useRouter, Link } from "../lib/router.js";
import { Button, Screen } from "../components/ui.js";
import { ChevronLeftIcon, GlobeIcon, LockIcon } from "../components/icons.js";

export function Join({ initialCode }: { initialCode?: string }) {
  const { token, nickname } = useSession();
  const { navigate } = useRouter();
  const [code, setCode] = useState(initialCode?.toUpperCase() ?? "");
  const [name, setName] = useState(nickname ?? "");
  const [game, setGame] = useState<GameSummary | null>(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // A share link (`/j/:code`) skips the "you're joining X's game" confirmation
  // card for public games and heads straight into joining — the standard
  // create-or-join screen is only useful when the visitor typed a code in
  // manually. Private games still need the PIN card regardless.
  const fromShareLink = !!initialCode;

  // If arriving from a share link, resolve the code immediately.
  useEffect(() => {
    if (initialCode) resolve(initialCode.toUpperCase());
  }, [initialCode]);

  // Public game via share link + we already know who you are -> join with no
  // extra tap at all.
  useEffect(() => {
    if (fromShareLink && game && game.visibility === "public" && token) doJoin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromShareLink, game, token]);

  async function resolve(c: string) {
    setError(null);
    setBusy(true);
    try {
      setGame(await api.resolveCode(c));
    } catch {
      setError("No game found for that code.");
      setGame(null);
    } finally {
      setBusy(false);
    }
  }

  async function doJoin() {
    if (!game) return;
    const nick = (name || nickname || "").trim();
    if (!nick) {
      setError("Enter your name first.");
      return;
    }
    if (game.visibility === "private" && !/^\d{4}$/.test(pin)) {
      setError("Enter the 4-digit PIN.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await useSession.getState().ensure(nick);
      const tok = useSession.getState().token!;
      await api.joinGame(game.id, tok, game.visibility === "private" ? pin : undefined);
      navigate(`/g/${game.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.code === "invalid_pin") setError("Wrong PIN — try again.");
      else if (e instanceof ApiError && e.code === "game_full") setError("That game is already full.");
      else setError("Couldn't join. Try again.");
      setBusy(false);
    }
  }

  // Public game reached via a share link: skip the "create or join game" /
  // confirmation-card flow entirely. Known nickname -> already joining (the
  // effect above fired). No nickname yet -> just ask for a name, no other copy.
  if (fromShareLink && game?.visibility === "public") {
    return (
      <Screen className="py-6">
        <h1 className="mb-1 text-2xl font-black">{game.hostNickname}'s game</h1>
        {token ? (
          <p className="mt-4 font-bold opacity-70">Joining…</p>
        ) : (
          <div className="mt-4">
            <label
              htmlFor="join-name"
              className="mb-1 block px-1 text-xs font-bold uppercase tracking-wide opacity-60"
            >
              Your name
            </label>
            <input
              id="join-name"
              name="nickname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={24}
              type="text"
              inputMode="text"
              enterKeyHint="go"
              autoComplete="off"
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Pick a name"
              onKeyDown={(e) => {
                if (e.key === "Enter") doJoin();
              }}
              className="clay-inset w-full bg-transparent px-4 py-3 text-lg font-bold outline-none"
              autoFocus
            />
            <Button variant="primary" className="mt-4 w-full" disabled={busy} onClick={doJoin}>
              {busy ? "Joining…" : "Join game"}
            </Button>
          </div>
        )}
        {error && <p className="mt-3 text-sm font-bold text-red-500">{error}</p>}
      </Screen>
    );
  }

  return (
    <Screen className="py-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm font-bold opacity-60">
        <ChevronLeftIcon className="h-4 w-4" /> Back
      </Link>
      <h1 className="mb-4 text-2xl font-black">Join a game</h1>

      {!token && (
        <div className="mb-4">
          <label
            htmlFor="join-name"
            className="mb-1 block px-1 text-xs font-bold uppercase tracking-wide opacity-60"
          >
            Your name
          </label>
          <input
            id="join-name"
            name="nickname"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={24}
            type="text"
            inputMode="text"
            enterKeyHint="next"
            autoComplete="off"
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Pick a name"
            className="clay-inset w-full bg-transparent px-4 py-3 text-lg font-bold outline-none"
          />
        </div>
      )}

      {!game && (
        <>
          <label
            htmlFor="join-code"
            className="mb-1 block px-1 text-xs font-bold uppercase tracking-wide opacity-60"
          >
            Game code
          </label>
          <div className="flex gap-2">
            <input
              id="join-code"
              name="joinCode"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="ABC123"
              type="text"
              inputMode="text"
              maxLength={6}
              enterKeyHint="go"
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              aria-label="Game code"
              onKeyDown={(e) => {
                if (e.key === "Enter" && code.length === 6) resolve(code);
              }}
              className="clay-inset min-w-0 flex-1 bg-transparent px-4 py-3 text-center text-2xl font-black tracking-[0.3em] outline-none"
            />
            <Button disabled={busy || code.length !== 6} onClick={() => resolve(code)}>
              Find
            </Button>
          </div>
        </>
      )}

      {game && (
        <div className="clay p-4">
          <p className="text-sm opacity-60">You're joining</p>
          <p className="text-xl font-black">{game.hostNickname}'s game</p>
          <p className="mt-1 flex items-center gap-1 text-xs opacity-60">
            {game.visibility === "private" ? (
              <LockIcon className="h-4 w-4" alt="" />
            ) : (
              <GlobeIcon className="h-4 w-4" alt="" />
            )}
            {game.visibility === "private" ? "Private" : "Public"} · code {game.joinCode}
          </p>
          {game.visibility === "private" && (
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              enterKeyHint="go"
              autoComplete="off"
              aria-label="4-digit game PIN"
              placeholder="PIN ••••"
              className="clay-inset mt-3 w-full bg-transparent px-4 py-3 text-center text-xl font-black tracking-[0.4em] outline-none"
            />
          )}
          <Button variant="primary" className="mt-4 w-full" disabled={busy} onClick={doJoin}>
            {busy ? "Joining…" : "Join game"}
          </Button>
        </div>
      )}

      {error && <p className="mt-3 text-sm font-bold text-red-500">{error}</p>}
    </Screen>
  );
}
